import { useId, useState } from "react";
import ChartTooltip from "./ChartTooltip";

/** Lightweight area chart for revenue / time series display */
export default function AreaChart({
  values = [],
  labels = [],
  points = null,
  height = 140,
  accent = "var(--chart-line)",
  className = "",
  interactive = true,
}) {
  const [hover, setHover] = useState(null);
  const gradId = useId().replace(/:/g, "");
  const nums = values.map((v) => (Number.isFinite(v) ? v : 0));
  const max = Math.max(1, ...nums);
  const w = 100;
  const h = 100;
  const pad = 4;
  const innerW = w - pad * 2;
  const innerH = h - pad * 2;

  const pointData =
    points ||
    nums.map((v, i) => ({
      label: labels[i] || String(i + 1),
      value: v,
    }));

  if (nums.every((v) => v === 0)) {
    return <div className={`area-chart area-chart--empty ${className}`}>No data in range</div>;
  }

  const coords = nums.map((v, i) => {
    const x = pad + (nums.length <= 1 ? innerW / 2 : (i / (nums.length - 1)) * innerW);
    const y = pad + innerH - (v / max) * innerH;
    return [x, y];
  });
  const line = coords.map((c, i) => `${i === 0 ? "M" : "L"} ${c[0]} ${c[1]}`).join(" ");
  const area = `${line} L ${coords[coords.length - 1][0]} ${pad + innerH} L ${coords[0][0]} ${pad + innerH} Z`;

  const setHoverAt = (i, clientX, clientY, hostRect) => {
    const p = pointData[i];
    if (!p) return;
    const [cx, cy] = coords[i];
    setHover({
      tip: {
        x: clientX - hostRect.left,
        y: clientY - hostRect.top,
        placement: "above",
        title: p.label,
        lines: [`${Number(p.value).toLocaleString()} sign-up${p.value === 1 ? "" : "s"}`],
      },
      dotLeft: `${(cx / w) * 100}%`,
      dotTop: `${(cy / h) * 100}%`,
    });
  };

  const handleMouseMove = (e) => {
    if (!interactive) return;
    const host = e.currentTarget.closest(".area-chart-plot");
    if (!host) return;
    const rect = host.getBoundingClientRect();
    const svg = host.querySelector("svg");
    if (!svg) return;
    const pt = svg.createSVGPoint();
    pt.x = e.clientX;
    pt.y = e.clientY;
    const ctm = svg.getScreenCTM();
    if (!ctm) return;
    const sp = pt.matrixTransform(ctm.inverse());
    const ratio = innerW / Math.max(1, nums.length - 1);
    const idx = Math.max(0, Math.min(nums.length - 1, Math.round((sp.x - pad) / ratio)));
    setHoverAt(idx, e.clientX, e.clientY, rect);
  };

  return (
    <div className={`area-chart ${className}`} style={{ position: "relative" }}>
      <div
        className="area-chart-plot"
        style={{ position: "relative", height }}
        onMouseMove={interactive ? handleMouseMove : undefined}
        onMouseLeave={interactive ? () => setHover(null) : undefined}
      >
        <svg
          viewBox={`0 0 ${w} ${h}`}
          preserveAspectRatio="none"
          className="area-chart-svg"
          style={{ height: "100%", width: "100%", display: "block" }}
        >
          <defs>
            <linearGradient id={gradId} x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor={accent} stopOpacity={0.35} />
              <stop offset="100%" stopColor={accent} stopOpacity={0.02} />
            </linearGradient>
          </defs>
          <path d={area} fill={`url(#${gradId})`} />
          <path d={line} fill="none" stroke={accent} strokeWidth={1.5} vectorEffect="non-scaling-stroke" />
        </svg>
        {hover && (
          <div
            className="area-chart-hover-dot"
            style={{ left: hover.dotLeft, top: hover.dotTop, background: accent }}
            aria-hidden
          />
        )}
      </div>
      {labels.length > 0 && (
        <div className="area-chart-labels">
          {labels.map((l) => (
            <span key={l}>{l}</span>
          ))}
        </div>
      )}
      <ChartTooltip tip={hover?.tip ?? null} />
    </div>
  );
}
